// @ts-nocheck -- inherited from lift-teams; pre-existing type issues outside this port's scope
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Input,
  SingleSelect,
  SpinnerLoader,
  TrashAlt,
  Toastify,
  GripVertical,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'lift-ui';
import React, { useEffect, useMemo, useRef, useState, useDeferredValue, useCallback } from 'react';

import { useAutoRowCreation } from '../hooks/useAutoRowCreation';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { useFilterState } from '../hooks/useFilterState';
import { BrandConfig, BrandThreshold } from '../types';
import { formatEventValue, parseEventValue } from '../utils/eventValueUtils';
import {
  filterThresholds,
  getBorderClasses,
  hasConnectionToNext as checkConnectionToNext,
  hasConnectionFromPrev as checkConnectionFromPrev,
} from '../utils/thresholdFilters';

import { FilterDropdown } from './FilterDropdown';

// Helper function to get error key for a threshold
const getThresholdErrorKey = (brandId: number, thresholdId: string) => `${brandId}-${thresholdId}`;

// Validation function for threshold values
export const validateThreshold = (
  value: string | number | null | undefined,
  maxValue?: number
): string | null => {
  if (value === '' || value === null || value === undefined) {
    return 'Threshold is required';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return 'Please enter a valid number';
  }
  if (numValue < 0) {
    return 'Threshold must be greater than or equal to 0';
  }
  if (maxValue !== undefined && numValue > maxValue) {
    return `Threshold must be less than or equal to ${maxValue}`;
  }
  return null;
};

// Types for drag-to-fill feature
type FillableColumn = 'pixel' | 'event' | 'operator' | 'threshold';

interface DragFillState {
  isActive: boolean;
  sourceRowIndex: number;
  sourceColumn: FillableColumn;
  targetRowIndices: number[];
  mouseY?: number;
}

interface CellSelectionState {
  rowIndex: number;
  column: FillableColumn;
}

// Sortable Threshold Row Component
interface SortableThresholdRowProps {
  threshold: BrandThreshold;
  index: number;
  originalIndex: number;
  isLastRow: boolean;
  hasConnectionToNext: boolean;
  hasConnectionFromPrev: boolean;
  children: React.ReactNode;
  logicalOperatorRow?: React.ReactNode;
}

const SortableThresholdRow: React.FC<SortableThresholdRowProps> = ({
  threshold,
  hasConnectionToNext,
  hasConnectionFromPrev,
  children,
  logicalOperatorRow,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: threshold.id,
  });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Clone the logical operator row and apply the same transform to it
  const transformedLogicalOperatorRow = logicalOperatorRow
    ? React.cloneElement(logicalOperatorRow as React.ReactElement, {
        style: {
          ...(logicalOperatorRow as React.ReactElement).props?.style,
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.5 : 1,
        },
      })
    : null;

  return (
    <React.Fragment>
      <tr ref={setNodeRef} style={rowStyle}>
        {/* Drag Handle Column */}
        <td
          className={`border-l border-r border-gray-300 ${hasConnectionFromPrev ? '' : 'border-t'} ${hasConnectionToNext ? '' : 'border-b'} px-1 py-2 bg-gray-50/50`}
        >
          <div className="flex items-center justify-center">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors duration-150 p-1 rounded hover:bg-gray-100"
              title="Drag to reorder"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          </div>
        </td>
        {children}
      </tr>
      {transformedLogicalOperatorRow}
    </React.Fragment>
  );
};

interface BrandThresholdTableProps {
  brandConfig: BrandConfig;
  pixels: Array<{
    id: number;
    brand_id: number;
    account_id: string;
    pixel_id: string;
    pixel_name: string;
    created_at: string;
    updated_at: string;
  }>;
  getEventsForThreshold: (threshold: BrandThreshold) => Array<{
    id: number;
    pixel_id_fk: number;
    event_name: string;
    composite_score: number | null;
    snapshot_date: string;
    created_at: string;
  }>;
  onPixelSelection: (brandId: number, thresholdIndex: number, pixelName: string) => Promise<void>;
  onUpdateThreshold: (
    brandId: number,
    thresholdIndex: number,
    field: keyof BrandThreshold,
    value: string | number
  ) => void;
  onAddThresholdRow: (brandId: number) => void;
  onRemoveThresholdRow: (brandId: number, thresholdIndex: number) => void;
  onReorderThresholds: (brandId: number, fromIndex: number, toIndex: number) => void;
  onClearAllThresholds: (brandId: number) => void;
  isLoadingEvents?: boolean;
  isLoadingInitialData?: boolean;
  fetchEventsForPixels?: (pixelIds: string[]) => Promise<void>;
  alertTypes?: Array<{
    id: number;
    name: string;
    display_name: string;
    description: string;
    data_source: string;
    created_at: string;
    updated_at: string;
    default_threshold_config?: Array<{
      value: number;
      metric: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      max_value?: string | number;
      period_days?: number;
    }>;
    alert_query_config?: {
      properties: string[];
    };
  }>;
  toolName?: string;
  onUpdateLogicalOperator?: (
    brandId: number,
    thresholdIndex: number,
    operator: 'AND' | 'OR' | 'NONE'
  ) => void;
}

export const BrandThresholdTable: React.FC<BrandThresholdTableProps> = ({
  brandConfig,
  pixels,
  getEventsForThreshold,
  onPixelSelection,
  onUpdateThreshold,
  onAddThresholdRow,
  onRemoveThresholdRow,
  onReorderThresholds,
  onClearAllThresholds,
  isLoadingEvents,
  isLoadingInitialData,
  alertTypes,
  toolName,
  onUpdateLogicalOperator,
  fetchEventsForPixels,
}) => {
  // Determine if inputs should be disabled
  const isDisabled = isLoadingInitialData || isLoadingEvents;
  // Use cleaner filter state management hook
  const { selectedPixels, selectedEvents, setSelectedPixels, setSelectedEvents } = useFilterState();

  // State to force re-render when events are fetched
  const [eventsFetchTrigger, setEventsFetchTrigger] = useState(0);

  // Drag-to-fill state
  const [selectedCell, setSelectedCell] = useState<CellSelectionState | null>(null);
  const [dragFillState, setDragFillState] = useState<DragFillState>({
    isActive: false,
    sourceRowIndex: -1,
    sourceColumn: 'pixel',
    targetRowIndices: [],
  });
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mouseYRef = useRef<number | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const dragDirectionRef = useRef<'up' | 'down' | null>(null);

  const { startAutoScroll, stopAutoScroll } = useAutoScroll({
    scrollContainerRef,
    isDraggingRef,
    mouseYRef,
    dragDirectionRef,
  });

  const cellRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  const [dragOverlay, setDragOverlay] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const getCellRefKey = (rowIndex: number, column: FillableColumn) => `${rowIndex}-${column}`;

  const registerCellRef = useCallback(
    (rowIndex: number, column: FillableColumn, el: HTMLTableCellElement | null) => {
      const key = getCellRefKey(rowIndex, column);
      if (el) {
        cellRefs.current.set(key, el);
      } else {
        cellRefs.current.delete(key);
      }
    },
    []
  );

  // Update overlay position when drag state changes
  useEffect(() => {
    if (!dragFillState.isActive) {
      setDragOverlay(null);
      return;
    }
    const sourceKey = getCellRefKey(dragFillState.sourceRowIndex, dragFillState.sourceColumn);
    const sourceCell = cellRefs.current.get(sourceKey);
    if (!sourceCell || !tableRef.current) {
      setDragOverlay(null);
      return;
    }
    const tableRect = tableRef.current.getBoundingClientRect();
    const sourceRect = sourceCell.getBoundingClientRect();
    if (dragFillState.targetRowIndices.length === 0) {
      setDragOverlay({
        top: sourceRect.top - tableRect.top,
        left: sourceRect.left - tableRect.left,
        width: sourceRect.width,
        height: sourceRect.height,
      });
      return;
    }
    const firstTargetIndex = dragFillState.targetRowIndices[0];
    const lastTargetIndex =
      dragFillState.targetRowIndices[dragFillState.targetRowIndices.length - 1];
    const isDraggingUp = firstTargetIndex < dragFillState.sourceRowIndex;
    if (isDraggingUp) {
      const firstTargetKey = getCellRefKey(firstTargetIndex, dragFillState.sourceColumn);
      const firstTargetCell = cellRefs.current.get(firstTargetKey);
      if (firstTargetCell) {
        const firstTargetRect = firstTargetCell.getBoundingClientRect();
        setDragOverlay({
          top: firstTargetRect.top - tableRect.top,
          left: sourceRect.left - tableRect.left,
          width: sourceRect.width,
          height: sourceRect.bottom - firstTargetRect.top,
        });
      }
    } else {
      const lastTargetKey = getCellRefKey(lastTargetIndex, dragFillState.sourceColumn);
      const lastTargetCell = cellRefs.current.get(lastTargetKey);
      if (lastTargetCell) {
        const lastTargetRect = lastTargetCell.getBoundingClientRect();
        setDragOverlay({
          top: sourceRect.top - tableRect.top,
          left: sourceRect.left - tableRect.left,
          width: sourceRect.width,
          height: lastTargetRect.bottom - sourceRect.top,
        });
      }
    }
  }, [dragFillState]);

  const handleCellClick = useCallback(
    (rowIndex: number, column: FillableColumn, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedCell({ rowIndex, column });
    },
    []
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tableRef.current && !tableRef.current.contains(e.target as Node)) {
        setSelectedCell(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFillHandleMouseDown = useCallback(
    (rowIndex: number, column: FillableColumn, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      mouseYRef.current = e.clientY;
      dragDirectionRef.current = null;
      setDragFillState({
        isActive: true,
        sourceRowIndex: rowIndex,
        sourceColumn: column,
        targetRowIndices: [],
        mouseY: e.clientY,
      });
    },
    []
  );

  const handleCellMouseEnter = useCallback(
    (rowIndex: number, column: FillableColumn) => {
      if (!dragFillState.isActive || column !== dragFillState.sourceColumn) return;
      if (rowIndex === dragFillState.sourceRowIndex) {
        setDragFillState(prev => ({ ...prev, targetRowIndices: [] }));
        return;
      }
      const indices: number[] = [];
      if (rowIndex > dragFillState.sourceRowIndex) {
        for (let i = dragFillState.sourceRowIndex + 1; i <= rowIndex; i++) indices.push(i);
      } else {
        for (let i = rowIndex; i < dragFillState.sourceRowIndex; i++) indices.push(i);
      }
      setDragFillState(prev => ({ ...prev, targetRowIndices: indices }));
    },
    [dragFillState.isActive, dragFillState.sourceColumn, dragFillState.sourceRowIndex]
  );

  const applyDragFill = useCallback(() => {
    if (!dragFillState.isActive || dragFillState.targetRowIndices.length === 0) {
      setDragFillState({
        isActive: false,
        sourceRowIndex: -1,
        sourceColumn: 'pixel',
        targetRowIndices: [],
        mouseY: undefined,
      });
      return;
    }
    const sourceThreshold = brandConfig.thresholds[dragFillState.sourceRowIndex];
    if (!sourceThreshold) {
      setDragFillState({
        isActive: false,
        sourceRowIndex: -1,
        sourceColumn: 'pixel',
        targetRowIndices: [],
        mouseY: undefined,
      });
      return;
    }
    let updatedRows = 0;
    dragFillState.targetRowIndices.forEach(targetIndex => {
      const targetThreshold = brandConfig.thresholds[targetIndex];
      switch (dragFillState.sourceColumn) {
        case 'pixel':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'pixelId',
            sourceThreshold.pixelId || ''
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'pixelName',
            sourceThreshold.pixelName || ''
          );
          onUpdateThreshold(brandConfig.brandId, targetIndex, 'eventId', 0);
          onUpdateThreshold(brandConfig.brandId, targetIndex, 'eventName', '');
          updatedRows++;
          break;
        case 'event':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'eventId',
            sourceThreshold.eventId || 0
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'eventName',
            sourceThreshold.eventName || ''
          );
          updatedRows++;
          break;
        case 'operator':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'operator',
            sourceThreshold.operator || '<'
          );
          updatedRows++;
          break;
        case 'threshold':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'threshold',
            sourceThreshold.threshold || 0
          );
          if (targetThreshold) {
            const errorKey = getThresholdErrorKey(brandConfig.brandId, targetThreshold.id);
            setLocalInputValues(prev => ({
              ...prev,
              [errorKey]: String(sourceThreshold.threshold || 0),
            }));
          }
          updatedRows++;
          break;
      }
    });
    if (updatedRows > 0) {
      Toastify('success', `Filled ${updatedRows} row(s)`);
    }
    setDragFillState({
      isActive: false,
      sourceRowIndex: -1,
      sourceColumn: 'pixel',
      targetRowIndices: [],
      mouseY: undefined,
    });
    setSelectedCell(null);
  }, [dragFillState, brandConfig.thresholds, brandConfig.brandId, onUpdateThreshold]);

  const calculateTargetRowsFromMouseY = useCallback(
    (mouseY: number): number[] => {
      if (!tableRef.current || !dragFillState.isActive) return [];
      const tableRect = tableRef.current.getBoundingClientRect();
      const relativeY = mouseY - tableRect.top;
      let targetRowIndex = -1;
      for (let i = 0; i < brandConfig.thresholds.length; i++) {
        const cellKey = getCellRefKey(i, dragFillState.sourceColumn);
        const cell = cellRefs.current.get(cellKey);
        if (cell) {
          const cellRect = cell.getBoundingClientRect();
          const cellRelativeTop = cellRect.top - tableRect.top;
          const cellRelativeBottom = cellRect.bottom - tableRect.top;
          if (relativeY >= cellRelativeTop && relativeY <= cellRelativeBottom) {
            targetRowIndex = i;
            break;
          }
          if (relativeY < cellRelativeTop && i > 0) {
            targetRowIndex = i - 1;
            break;
          }
        }
      }
      if (targetRowIndex === -1 && brandConfig.thresholds.length > 0) {
        const lastIndex = brandConfig.thresholds.length - 1;
        const lastCellKey = getCellRefKey(lastIndex, dragFillState.sourceColumn);
        const lastCell = cellRefs.current.get(lastCellKey);
        if (lastCell && mouseY > lastCell.getBoundingClientRect().bottom) {
          targetRowIndex = lastIndex;
        }
      }
      if (targetRowIndex === -1 || targetRowIndex === dragFillState.sourceRowIndex) return [];
      const indices: number[] = [];
      if (targetRowIndex > dragFillState.sourceRowIndex) {
        for (let i = dragFillState.sourceRowIndex + 1; i <= targetRowIndex; i++) indices.push(i);
      } else {
        for (let i = targetRowIndex; i < dragFillState.sourceRowIndex; i++) indices.push(i);
      }
      return indices;
    },
    [
      dragFillState.isActive,
      dragFillState.sourceRowIndex,
      dragFillState.sourceColumn,
      brandConfig.thresholds.length,
    ]
  );

  useEffect(() => {
    if (!dragFillState.isActive) {
      isDraggingRef.current = false;
      mouseYRef.current = null;
      stopAutoScroll();
      return;
    }
    let rafId: number | null = null;
    let hasMovedMouse = false;
    const initialMouseY = dragFillState.mouseY;
    const updateOverlayFromMousePosition = () => {
      if (!dragFillState.isActive || mouseYRef.current === null) return;
      const targetIndices = calculateTargetRowsFromMouseY(mouseYRef.current);
      setDragFillState(prev => {
        if (
          prev.targetRowIndices.length !== targetIndices.length ||
          prev.targetRowIndices.some((idx, i) => idx !== targetIndices[i])
        ) {
          return { ...prev, targetRowIndices: targetIndices };
        }
        return prev;
      });
      rafId = window.requestAnimationFrame(updateOverlayFromMousePosition);
    };
    const handleMouseMove = (e: MouseEvent) => {
      mouseYRef.current = e.clientY;
      if (initialMouseY !== undefined) {
        if (e.clientY > initialMouseY + 5) dragDirectionRef.current = 'down';
        else if (e.clientY < initialMouseY - 5) dragDirectionRef.current = 'up';
      }
      if (!hasMovedMouse && initialMouseY !== undefined) {
        if (Math.abs(e.clientY - initialMouseY) > 10) {
          hasMovedMouse = true;
          startAutoScroll();
        }
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    rafId = window.requestAnimationFrame(updateOverlayFromMousePosition);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      isDraggingRef.current = false;
      mouseYRef.current = null;
      dragDirectionRef.current = null;
      stopAutoScroll();
    };
  }, [
    dragFillState.isActive,
    dragFillState.mouseY,
    startAutoScroll,
    stopAutoScroll,
    calculateTargetRowsFromMouseY,
  ]);

  useEffect(() => {
    const handleMouseUp = () => {
      if (dragFillState.isActive) {
        isDraggingRef.current = false;
        mouseYRef.current = null;
        dragDirectionRef.current = null;
        stopAutoScroll();
        applyDragFill();
      }
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [dragFillState.isActive, applyDragFill, stopAutoScroll]);

  const isCellInFillRange = (rowIndex: number, column: FillableColumn) => {
    return (
      dragFillState.isActive &&
      dragFillState.sourceColumn === column &&
      dragFillState.targetRowIndices.includes(rowIndex)
    );
  };

  useEffect(() => {
    return () => {
      stopAutoScroll();
      isDraggingRef.current = false;
      mouseYRef.current = null;
    };
  }, [stopAutoScroll]);

  // State to track validation errors for thresholds
  const [thresholdErrors, setThresholdErrors] = useState<Record<string, string>>({});

  // State to track local input values (allows empty string while typing)
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});

  // State for confirmation dialog when unselecting pixels
  const [pendingPixelUnselection, setPendingPixelUnselection] = useState<{
    newSelectedPixels: string[];
    removedPixels: string[];
    affectedThresholdCount: number;
  } | null>(null);

  // Track previous thresholds to detect deletions
  const prevThresholdsRef = useRef<BrandThreshold[]>(brandConfig.thresholds);
  // Track previous pixel selections so we can remove orphaned event filters
  const prevSelectedPixelsRef = useRef<string[]>(selectedPixels);

  const getThresholdEventValue = useCallback((threshold: BrandThreshold): string | null => {
    if (!threshold.eventId || !threshold.eventName) {
      return null;
    }
    return formatEventValue(threshold.eventId, threshold.eventName, threshold.pixelId || undefined);
  }, []);

  const ensurePixelFilter = useCallback(
    (pixelId?: string) => {
      if (!pixelId) return;
      setSelectedPixels(prev => (prev.includes(pixelId) ? prev : [...prev, pixelId]));
    },
    [setSelectedPixels]
  );

  const ensureEventFilter = useCallback(
    (eventValue: string, pixelId?: string) => {
      if (!eventValue) return;
      setSelectedEvents(prev => (prev.includes(eventValue) ? prev : [...prev, eventValue]));
      ensurePixelFilter(pixelId);
    },
    [ensurePixelFilter, setSelectedEvents]
  );

  // When pixels are deselected, remove any event filters tied to those pixels
  useEffect(() => {
    const prevPixels = prevSelectedPixelsRef.current;
    const removedPixels = prevPixels.filter(pixelId => !selectedPixels.includes(pixelId));

    if (removedPixels.length > 0) {
      setSelectedEvents(prevEvents => {
        const filteredEvents = prevEvents.filter(eventValue => {
          const parsed = parseEventValue(eventValue);
          if (!parsed?.pixelId) {
            return true;
          }
          return !removedPixels.includes(parsed.pixelId);
        });

        return filteredEvents.length === prevEvents.length ? prevEvents : filteredEvents;
      });
    }

    prevSelectedPixelsRef.current = selectedPixels;
  }, [selectedPixels, setSelectedEvents]);

  const removeEventFilterIfUnused = useCallback(
    (eventValue: string | null, thresholdId?: string) => {
      if (!eventValue) return;
      const stillUsed = brandConfig.thresholds.some(threshold => {
        if (thresholdId && threshold.id === thresholdId) {
          return false;
        }
        return getThresholdEventValue(threshold) === eventValue;
      });
      if (!stillUsed) {
        setSelectedEvents(prev => prev.filter(value => value !== eventValue));
      }
    },
    [brandConfig.thresholds, getThresholdEventValue, setSelectedEvents]
  );

  // Calculate how many thresholds would be affected by unselecting pixels
  const calculateAffectedThresholds = useCallback(
    (removedPixels: string[]): number => {
      if (removedPixels.length === 0) return 0;

      // Count thresholds that use any of the removed pixels
      const affectedThresholds = brandConfig.thresholds.filter(threshold => {
        if (!threshold.pixelId) return false;
        return removedPixels.includes(threshold.pixelId);
      });

      return affectedThresholds.length;
    },
    [brandConfig.thresholds]
  );

  // Handle pixel selection change with confirmation dialog
  const handlePixelSelectionChange = useCallback(
    (newSelectedPixels: string[]) => {
      const removedPixels = selectedPixels.filter(pixelId => !newSelectedPixels.includes(pixelId));

      // If pixels are being removed, check if any thresholds would be affected
      if (removedPixels.length > 0) {
        const affectedCount = calculateAffectedThresholds(removedPixels);
        if (affectedCount > 0) {
          // Show confirmation dialog
          setPendingPixelUnselection({
            newSelectedPixels,
            removedPixels,
            affectedThresholdCount: affectedCount,
          });
          return;
        }
      }

      // No thresholds affected, proceed with selection change
      setSelectedPixels(newSelectedPixels);
    },
    [selectedPixels, calculateAffectedThresholds, setSelectedPixels]
  );

  // Confirm pixel unselection
  const confirmPixelUnselection = useCallback(() => {
    if (pendingPixelUnselection) {
      setSelectedPixels(pendingPixelUnselection.newSelectedPixels);
      setPendingPixelUnselection(null);
    }
  }, [pendingPixelUnselection, setSelectedPixels]);

  // Cancel pixel unselection
  const cancelPixelUnselection = useCallback(() => {
    setPendingPixelUnselection(null);
  }, []);

  // Track which pixels we've fetched events for (to avoid duplicate fetches)
  const fetchedPixelsRef = useRef<Set<string>>(new Set());

  // Get default operator and threshold from alert type
  const defaultOperator = useMemo(
    () =>
      (alertTypes?.find(at => at.display_name === toolName)?.default_threshold_config?.[0]
        ?.operator || '<') as '<' | '>' | '<=' | '>=' | '==' | '!=',
    [alertTypes, toolName]
  );

  const defaultThreshold = useMemo(
    () =>
      alertTypes?.find(at => at.display_name === toolName)?.default_threshold_config?.[0]?.value ||
      0,
    [alertTypes, toolName]
  );

  // Get max_value from alert type for validation
  const maxValue = useMemo(
    () =>
      alertTypes?.find(at => at.display_name === toolName)?.default_threshold_config?.[0]
        ?.max_value !== undefined
        ? parseFloat(
            String(
              alertTypes.find(at => at.display_name === toolName)?.default_threshold_config?.[0]
                ?.max_value
            )
          )
        : undefined,
    [alertTypes, toolName]
  );

  // Validate all thresholds and update errors state (for display purposes)
  useEffect(() => {
    const errors: Record<string, string> = {};
    brandConfig.thresholds.forEach(threshold => {
      const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
      const error = validateThreshold(threshold.threshold, maxValue);
      if (error) {
        errors[errorKey] = error;
      }
    });

    // Update errors state only if changed
    setThresholdErrors(prev => {
      const hasChanges =
        Object.keys(errors).length !== Object.keys(prev).length ||
        Object.keys(errors).some(key => errors[key] !== prev[key]);
      return hasChanges ? errors : prev;
    });
  }, [brandConfig.thresholds, brandConfig.brandId, maxValue]);

  // Track initialized input keys to avoid overwriting user input
  const initializedKeysRef = useRef<Set<string>>(new Set());

  // Sync local input values when thresholds change externally (only for new thresholds)
  useEffect(() => {
    const updatedValues: Record<string, string> = {};
    brandConfig.thresholds.forEach(threshold => {
      const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
      // Only initialize if not already initialized (to preserve user's typing)
      if (!initializedKeysRef.current.has(errorKey)) {
        if (threshold.threshold !== null && threshold.threshold !== undefined) {
          updatedValues[errorKey] = String(threshold.threshold);
          initializedKeysRef.current.add(errorKey);
        }
      }
    });
    if (Object.keys(updatedValues).length > 0) {
      setLocalInputValues(prev => ({ ...prev, ...updatedValues }));
    }
  }, [brandConfig.thresholds, brandConfig.brandId]);

  // Defer heavy auto-row creation to keep UI responsive
  const deferredSelectedEvents = useDeferredValue(selectedEvents);

  // Use auto-row creation hook to handle complex row creation logic
  const { cleanupDeletedThreshold } = useAutoRowCreation({
    brandConfig,
    selectedEvents: deferredSelectedEvents,
    pixels,
    getEventsForThreshold,
    onAddThresholdRow,
    onRemoveThresholdRow,
    onUpdateThreshold,
    onPixelSelection,
    defaultOperator,
    defaultThreshold,
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = brandConfig.thresholds.findIndex(t => t.id === active.id);
      const newIndex = brandConfig.thresholds.findIndex(t => t.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderThresholds(brandConfig.brandId, oldIndex, newIndex);
      }
    }
  };

  // Auto-populate eventId in thresholds when events are loaded
  useEffect(() => {
    brandConfig.thresholds.forEach((threshold, thresholdIndex) => {
      // If we have eventName and pixelId but no eventId, try to find and populate it
      if (threshold.eventName && threshold.pixelId && !threshold.eventId) {
        const eventsForPixel = getEventsForThreshold(threshold);
        const matchingEvent = eventsForPixel.find(e => e.event_name === threshold.eventName);
        if (matchingEvent) {
          // Update the threshold with the eventId
          onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'eventId', matchingEvent.id);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandConfig.thresholds, getEventsForThreshold]);

  /**
   * Fetch events for pixels selected in the filter
   * Uses direct event fetching without manipulating thresholds
   */
  useEffect(() => {
    if (!fetchEventsForPixels) return;

    if (selectedPixels.length > 0) {
      // Filter out pixels we've already fetched
      const pixelsToFetch = selectedPixels.filter(pixelId => {
        // Check if events already exist for this pixel
        const pixel = pixels.find(p => p.pixel_id === pixelId);
        if (!pixel) return false;

        const tempThreshold: BrandThreshold = {
          id: 'temp-check',
          pixelId: pixelId,
          threshold: 0,
          operator: '<',
        };
        const existingEvents = getEventsForThreshold(tempThreshold);

        // If events exist, mark as fetched and skip
        if (existingEvents.length > 0) {
          fetchedPixelsRef.current.add(pixelId);
          return false;
        }

        // Skip if already fetched
        if (fetchedPixelsRef.current.has(pixelId)) {
          return false;
        }

        return true;
      });

      // Fetch events for pixels that need it
      if (pixelsToFetch.length > 0) {
        fetchEventsForPixels(pixelsToFetch).then(() => {
          // Mark as fetched
          pixelsToFetch.forEach(pixelId => fetchedPixelsRef.current.add(pixelId));
          // Trigger re-render to update event dropdown
          setEventsFetchTrigger(prev => prev + 1);
        });
      }
    } else {
      // Clear fetched pixels when filter is cleared
      fetchedPixelsRef.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPixels, fetchEventsForPixels]);

  /**
   * Auto-row creation logic is now handled by useAutoRowCreation hook
   * This simplifies the component by separating the complex creation/deletion logic
   */

  // Ensure there's always at least one empty row
  useEffect(() => {
    // Check if there are any rows at all
    if (brandConfig.thresholds.length === 0) {
      onAddThresholdRow(brandConfig.brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandConfig.thresholds.length]);

  /**
   * Clean up auto-created tracking and filter selections when rows are deleted
   */
  useEffect(() => {
    const prevThresholds = prevThresholdsRef.current;
    const currentThresholds = brandConfig.thresholds;

    // If thresholds decreased (row was deleted)
    if (currentThresholds.length < prevThresholds.length) {
      // Find which thresholds were deleted by comparing IDs
      const currentIds = new Set(currentThresholds.map(t => t.id));
      const deletedThresholds = prevThresholds.filter(t => !currentIds.has(t.id));

      // Clean up auto-created tracking
      deletedThresholds.forEach(deletedThreshold => {
        if (deletedThreshold.id) {
          cleanupDeletedThreshold(deletedThreshold.id);
        }
      });

      // Update event filters to reflect remaining rows
      const currentEventValues = new Set(
        currentThresholds
          .map(getThresholdEventValue)
          .filter((value): value is string => Boolean(value))
      );

      setSelectedEvents(prev => prev.filter(eventValue => currentEventValues.has(eventValue)));

      // Do not automatically remove pixel filters; leave them for manual user adjustments
    }

    // Ensure filters stay aligned with current thresholds
    currentThresholds.forEach(currentThreshold => {
      const previousThreshold = prevThresholds.find(t => t.id === currentThreshold.id);
      const currentValue = getThresholdEventValue(currentThreshold);
      const previousValue = previousThreshold ? getThresholdEventValue(previousThreshold) : null;

      if (currentValue && currentValue !== previousValue) {
        ensureEventFilter(currentValue, currentThreshold.pixelId);
      }

      if (previousValue && previousValue !== currentValue) {
        removeEventFilterIfUnused(previousValue, currentThreshold.id);
      }
    });

    // Update the ref for next comparison
    prevThresholdsRef.current = currentThresholds;
  }, [
    brandConfig.thresholds,
    cleanupDeletedThreshold,
    ensureEventFilter,
    getThresholdEventValue,
    removeEventFilterIfUnused,
    setSelectedEvents,
    setSelectedPixels,
  ]);

  // Color palette for pixel badges
  const badgeColors = [
    'blue',
    'green',
    'purple',
    'orange',
    'pink',
    'indigo',
    'teal',
    'cyan',
    'amber',
  ];

  // Function to get a consistent color for each pixel
  const getPixelColor = (pixelId: string) => {
    // Create a simple hash from the pixel ID
    let hash = 0;
    for (let i = 0; i < pixelId.length; i++) {
      hash = pixelId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % badgeColors.length;
    return badgeColors[index];
  };

  // Get pixel options for the dropdown
  const pixelOptions = pixels.map(pixel => ({
    label: pixel.pixel_name,
    value: pixel.pixel_id,
  }));

  // Get all unique events from thresholds that match selected pixels
  const getAllAvailableEvents = () => {
    // Use a composite key to allow same event from different pixels
    const eventMap = new Map<
      string,
      { id: number; event_name: string; pixel_name?: string; pixel_id?: string }
    >();

    // If pixels are selected in the filter, get events from those specific pixels
    if (selectedPixels.length > 0) {
      selectedPixels.forEach(pixelId => {
        const pixel = pixels.find(p => p.pixel_id === pixelId);
        const pixelName = pixel?.pixel_name || '';

        const tempThreshold: BrandThreshold = {
          id: 'temp',
          pixelId: pixelId,
          threshold: 0,
          operator: '<',
        };
        const eventsForPixel = getEventsForThreshold(tempThreshold);

        eventsForPixel.forEach(event => {
          const eventKey = formatEventValue(event.id, event.event_name, pixelId);
          if (!eventMap.has(eventKey)) {
            eventMap.set(eventKey, {
              id: event.id,
              event_name: event.event_name,
              pixel_name: pixelName,
              pixel_id: pixelId,
            });
          }
        });
      });
    } else {
      // No pixel filters selected, so no events should be available
      return [];
    }

    return Array.from(eventMap.values()).sort((a, b) => a.event_name.localeCompare(b.event_name));
  };

  // Use eventsFetchTrigger to force recalculation when events are fetched
  const availableEvents = React.useMemo(() => {
    return getAllAvailableEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPixels, eventsFetchTrigger, pixels]);

  // Get event options for the dropdown
  const eventOptions = availableEvents.map(event => ({
    label: `${event.event_name}`,
    value: formatEventValue(event.id, event.event_name, event.pixel_id),
    badge: event.pixel_name,
    badgeColor: event.pixel_id ? getPixelColor(event.pixel_id) : 'blue',
  }));

  // Filter thresholds based on selected pixels and events
  // Uses utility function for cleaner, testable code
  const filteredThresholds = useMemo(
    () => filterThresholds(brandConfig.thresholds, selectedPixels, selectedEvents),
    [brandConfig.thresholds, selectedPixels, selectedEvents]
  );

  // Handle clear all with filter reset
  const handleClearAll = () => {
    // Clear all thresholds
    onClearAllThresholds(brandConfig.brandId);
    // Clear filter selections
    setSelectedPixels([]);
    setSelectedEvents([]);
    // Clear fetched pixels tracking
    fetchedPixelsRef.current.clear();
    // Reset previous thresholds ref
    prevThresholdsRef.current = [];
  };

  return (
    <div className="mb-6 w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Brand-Specific Thresholds ({brandConfig.brandName})
        </h3>
        {brandConfig.thresholds.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClearAll}
            disabled={isDisabled}
            className="text-xs"
          >
            Clear All
          </Button>
        )}
      </div>

      <div
        ref={scrollContainerRef}
        className="overflow-x-auto w-full relative"
        style={{ maxHeight: '600px', overflowY: 'auto' }}
      >
        {/* Drag Fill Selection Overlay */}
        {dragOverlay && dragFillState.isActive && (
          <div
            className="absolute pointer-events-none z-20 border-2 border-blue-500 bg-blue-500/10 rounded-sm"
            style={{
              top: dragOverlay.top,
              left: dragOverlay.left,
              width: dragOverlay.width,
              height: dragOverlay.height,
              transition: 'height 0.05s ease-out',
            }}
          >
            {dragFillState.targetRowIndices.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500" />
            )}
            {dragFillState.targetRowIndices.length > 0 && (
              <div className="absolute -right-8 top-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-lg">
                {dragFillState.targetRowIndices.length}
              </div>
            )}
          </div>
        )}
        <table ref={tableRef} className="border-collapse border border-gray-300 w-full">
          <colgroup>
            <col style={{ width: '40px' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-1 py-2 text-center text-sm font-medium text-gray-700">
                <div className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3 w-3 text-gray-400"
                  >
                    <circle cx="9" cy="5" r="1" />
                    <circle cx="9" cy="12" r="1" />
                    <circle cx="9" cy="19" r="1" />
                    <circle cx="15" cy="5" r="1" />
                    <circle cx="15" cy="12" r="1" />
                    <circle cx="15" cy="19" r="1" />
                  </svg>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                <div className="flex items-center">
                  <span>Pixel</span>
                  <span className="text-red-500 ml-1">*</span>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                <div className="flex items-center">
                  <span>Event</span>
                  <span className="text-red-500 ml-1">*</span>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                Operator
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                Enter Threshold
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">
                Actions
              </th>
            </tr>
            {/* Filter Row */}
            <tr className="bg-gray-50">
              <td className="border border-gray-300 px-2 py-2"></td>
              <td className="border border-gray-300 px-2 py-2">
                <div className="w-full" onClick={e => e.stopPropagation()}>
                  <FilterDropdown
                    selectedValues={selectedPixels}
                    onSelectionChange={handlePixelSelectionChange}
                    options={pixelOptions}
                    placeholder="Select Pixel"
                    getDisplayLabel={value => {
                      const pixel = pixels.find(p => p.pixel_id === value);
                      return pixel?.pixel_name || value;
                    }}
                  />
                </div>
              </td>
              <td className="border border-gray-300 px-2 py-2">
                <div className="w-full" onClick={e => e.stopPropagation()}>
                  <FilterDropdown
                    selectedValues={selectedEvents}
                    onSelectionChange={setSelectedEvents}
                    options={eventOptions}
                    placeholder="Select Event"
                    emptyMessage="Please select a pixel with events to select an event"
                    getDisplayLabel={value => {
                      const parsed = parseEventValue(value);
                      return parsed?.eventName || value;
                    }}
                  />
                </div>
              </td>
              <td className="border border-gray-300 px-2 py-2"></td>
              <td className="border border-gray-300 px-2 py-2"></td>
              <td className="border border-gray-300 px-2 py-2 text-center"></td>
            </tr>
          </thead>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            autoScroll={false}
          >
            <SortableContext
              items={brandConfig.thresholds.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {filteredThresholds.map((threshold, index) => {
                  // Find the original index in brandConfig.thresholds
                  const originalIndex = brandConfig.thresholds.findIndex(
                    t => t.id === threshold.id
                  );
                  const isLastRow = index === filteredThresholds.length - 1;

                  // Use utility functions for cleaner logic
                  const prevThreshold = index > 0 ? filteredThresholds[index - 1] : null;
                  const connToNext = checkConnectionToNext(threshold, isLastRow);
                  const connFromPrev = checkConnectionFromPrev(prevThreshold);

                  // Get border classes from utility function
                  const { firstColBorderClass, middleColBorderClass, lastColBorderClass } =
                    getBorderClasses(connFromPrev, connToNext);

                  const logicalOperatorRow = !isLastRow && (
                    <tr>
                      <td
                        colSpan={6}
                        className="border-x border-gray-300 px-4 py-3 bg-gray-50/30"
                        style={{ borderTop: 'none', borderBottom: 'none' }}
                      >
                        <div className="flex items-center relative pl-8" style={{ height: '56px' }}>
                          {/* Curly brace connector - only show if AND or OR */}
                          {threshold.logicalOperator && threshold.logicalOperator !== 'NONE' && (
                            <div
                              className="absolute flex flex-col items-center"
                              style={{ height: '100%', width: '24px', left: '16px' }}
                            >
                              {/* Top curve */}
                              <div
                                className={`w-full h-1/2 border-l-2 border-b-2 rounded-bl-lg ${
                                  threshold.logicalOperator === 'AND'
                                    ? 'border-green-500'
                                    : 'border-orange-500'
                                }`}
                                style={{ borderBottomLeftRadius: '10px' }}
                              />
                              {/* Bottom curve */}
                              <div
                                className={`w-full h-1/2 border-l-2 border-t-2 rounded-tl-lg ${
                                  threshold.logicalOperator === 'AND'
                                    ? 'border-green-500'
                                    : 'border-orange-500'
                                }`}
                                style={{ borderTopLeftRadius: '10px' }}
                              />
                            </div>
                          )}

                          {/* Logical Operator Selector */}
                          <div className="flex items-center gap-3 ml-8">
                            <SingleSelect
                              key={`${threshold.id}-logical-op`}
                              defaultValue={threshold.logicalOperator || 'NONE'}
                              disabled={isDisabled}
                              onChange={(value: string) => {
                                if (onUpdateLogicalOperator) {
                                  onUpdateLogicalOperator(
                                    brandConfig.brandId,
                                    originalIndex,
                                    value as 'AND' | 'OR' | 'NONE'
                                  );
                                }
                              }}
                              options={[
                                { label: 'AND', value: 'AND' },
                                { label: 'OR', value: 'OR' },
                                { label: 'NONE', value: 'NONE' },
                              ]}
                              placeholder="Select Operator"
                              containerClassName="w-36"
                            />

                            {/* Visual indicator badge */}
                            {threshold.logicalOperator === 'AND' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-green-100 text-green-700 rounded-md shadow-sm">
                                Both threshold conditions must meet
                              </span>
                            )}
                            {threshold.logicalOperator === 'OR' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded-md shadow-sm">
                                Any threshold condition meets
                              </span>
                            )}
                            {threshold.logicalOperator === 'NONE' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-md shadow-sm">
                                No connection
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );

                  return (
                    <SortableThresholdRow
                      key={threshold.id}
                      threshold={threshold}
                      index={index}
                      originalIndex={originalIndex}
                      isLastRow={isLastRow}
                      hasConnectionToNext={connToNext}
                      hasConnectionFromPrev={connFromPrev}
                      logicalOperatorRow={logicalOperatorRow}
                    >
                      <td
                        ref={el => registerCellRef(originalIndex, 'pixel', el)}
                        className={`${firstColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'pixel'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'pixel') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'pixel', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'pixel')}
                      >
                        <div className="w-full overflow-hidden">
                          <SingleSelect
                            key={`${threshold.id}-pixel-${threshold.pixelId || 'empty'}`}
                            defaultValue={threshold.pixelId || ''}
                            disabled={isDisabled}
                            onChange={async (value: string) => {
                              if (!value || value.trim() === '') {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'pixelId',
                                  ''
                                );
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'pixelName',
                                  ''
                                );
                                onUpdateThreshold(brandConfig.brandId, originalIndex, 'eventId', 0);
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'eventName',
                                  ''
                                );
                              } else {
                                const currentPixelId = threshold.pixelId || '';
                                if (currentPixelId !== value) {
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'eventId',
                                    0
                                  );
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'eventName',
                                    ''
                                  );
                                }
                                await onPixelSelection(brandConfig.brandId, originalIndex, value);
                                setEventsFetchTrigger(prev => prev + 1);
                              }
                            }}
                            options={pixels.map(pixel => ({
                              label: pixel.pixel_name,
                              value: pixel.pixel_id,
                            }))}
                            placeholder="Select Pixel"
                            containerClassName="w-full"
                            maxWidth={270}
                          />
                        </div>
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'pixel' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'pixel', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>
                      <td
                        ref={el => registerCellRef(originalIndex, 'event', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'event'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'event') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'event', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'event')}
                      >
                        <div className="w-full overflow-hidden">
                          {(() => {
                            const thresholdForEvents: BrandThreshold = {
                              ...threshold,
                              pixelId: threshold.pixelId || '',
                            };
                            const eventsForThreshold = getEventsForThreshold(thresholdForEvents);

                            let filteredEvents = eventsForThreshold;
                            if (selectedPixels.length > 0) {
                              const isManuallyEdited = threshold.pixelId || threshold.eventId;
                              if (
                                !selectedPixels.includes(threshold.pixelId || '') &&
                                !isManuallyEdited
                              ) {
                                filteredEvents = [];
                              }
                            }
                            const options = filteredEvents.map(event => ({
                              label: `${event.event_name}`,
                              value: formatEventValue(
                                event.id,
                                event.event_name,
                                threshold.pixelId || undefined
                              ),
                            }));

                            const selectedEventValue = getThresholdEventValue(threshold) || '';

                            return (
                              <SingleSelect
                                key={`${threshold.id}-pixel-${threshold.pixelId || 'empty'}-event-${eventsFetchTrigger}`}
                                defaultValue={selectedEventValue}
                                disabled={isDisabled}
                                onChange={(value: string) => {
                                  const previousValue = getThresholdEventValue(threshold);

                                  if (!value || value.trim() === '') {
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'eventId',
                                      0
                                    );
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'eventName',
                                      ''
                                    );
                                    removeEventFilterIfUnused(previousValue, threshold.id);
                                    return;
                                  }

                                  const parsed = parseEventValue(value);
                                  if (!parsed) return;

                                  const currentThresholdForEvents: BrandThreshold = {
                                    ...threshold,
                                    pixelId: threshold.pixelId || '',
                                  };
                                  const currentEventsForThreshold =
                                    getEventsForThreshold(currentThresholdForEvents);
                                  const selectedEvent = currentEventsForThreshold.find(
                                    e => e.id === parsed.eventId
                                  );
                                  if (!selectedEvent) return;

                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'eventId',
                                    selectedEvent.id
                                  );
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'eventName',
                                    selectedEvent.event_name
                                  );

                                  const newValue = formatEventValue(
                                    selectedEvent.id,
                                    selectedEvent.event_name,
                                    threshold.pixelId || parsed.pixelId
                                  );

                                  ensureEventFilter(newValue, threshold.pixelId || parsed.pixelId);
                                  if (previousValue && previousValue !== newValue) {
                                    removeEventFilterIfUnused(previousValue, threshold.id);
                                  }
                                }}
                                options={options}
                                placeholder="Select Event"
                                containerClassName="w-full"
                                maxWidth={270}
                              />
                            );
                          })()}
                        </div>
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'event' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'event', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>
                      <td
                        ref={el => registerCellRef(originalIndex, 'operator', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'operator'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'operator') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'operator', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'operator')}
                      >
                        <div className="w-full overflow-hidden">
                          <SingleSelect
                            key={`${threshold.id}-operator-${threshold.operator}`}
                            defaultValue={threshold.operator || '<'}
                            disabled={isDisabled}
                            onChange={(value: string) => {
                              onUpdateThreshold(
                                brandConfig.brandId,
                                originalIndex,
                                'operator',
                                value as '<' | '>' | '<=' | '>=' | '==' | '!='
                              );
                            }}
                            options={[
                              { label: '<', value: '<' },
                              { label: '>', value: '>' },
                              { label: '<=', value: '<=' },
                              { label: '>=', value: '>=' },
                              { label: '==', value: '==' },
                              { label: '!=', value: '!=' },
                            ]}
                            placeholder="Select Operator"
                            containerClassName="w-full"
                            maxWidth={120}
                            clearIconClassName="hidden"
                          />
                        </div>
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'operator' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'operator', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>
                      <td
                        ref={el => registerCellRef(originalIndex, 'threshold', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'threshold'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'threshold') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'threshold', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'threshold')}
                      >
                        {(() => {
                          const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
                          const localValue =
                            localInputValues[errorKey] !== undefined
                              ? localInputValues[errorKey]
                              : threshold.threshold !== null && threshold.threshold !== undefined
                                ? String(threshold.threshold)
                                : '';

                          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                            const inputValue = e.target.value;
                            initializedKeysRef.current.add(errorKey);
                            setLocalInputValues(prev => ({ ...prev, [errorKey]: inputValue }));
                            if (inputValue !== '') {
                              const numValue = parseFloat(inputValue);
                              if (!isNaN(numValue)) {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'threshold',
                                  numValue
                                );
                              }
                            }
                          };

                          const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                            const inputValue = e.target.value;
                            const numValue = inputValue === '' ? 0 : parseFloat(inputValue);
                            if (inputValue === '') {
                              onUpdateThreshold(brandConfig.brandId, originalIndex, 'threshold', 0);
                              setLocalInputValues(prev => ({ ...prev, [errorKey]: '0' }));
                            } else if (!isNaN(numValue)) {
                              setLocalInputValues(prev => ({
                                ...prev,
                                [errorKey]: String(numValue),
                              }));
                            }
                            const validationError = validateThreshold(numValue, maxValue);
                            if (validationError) {
                              Toastify('error', validationError);
                              setThresholdErrors(prev => ({
                                ...prev,
                                [errorKey]: validationError,
                              }));
                            } else {
                              setThresholdErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[errorKey];
                                return newErrors;
                              });
                            }
                          };

                          return (
                            <div className="w-full">
                              <Input
                                type="number"
                                value={localValue}
                                disabled={isDisabled}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter threshold"
                                step="any"
                                className="w-full"
                              />
                            </div>
                          );
                        })()}
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'threshold' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'threshold', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>
                      <td className={`${lastColBorderClass} px-2 py-2 text-center`}>
                        <button
                          onClick={() => {
                            // Delete the row (cleanup of events and filter selections is handled in hooks/useEffect)
                            onRemoveThresholdRow(brandConfig.brandId, originalIndex);
                            // Trigger re-render to update the UI
                            setTimeout(() => {
                              setEventsFetchTrigger(prev => prev + 1);
                            }, 100);
                          }}
                          disabled={brandConfig.thresholds.length === 1}
                          className={`${
                            brandConfig.thresholds.length === 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-red-500 hover:text-red-700'
                          }`}
                        >
                          <TrashAlt className="h-4 w-4" />
                        </button>
                      </td>
                    </SortableThresholdRow>
                  );
                })}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>

      <div className="flex justify-between mt-4">
        <Button variant="primary" onClick={() => onAddThresholdRow(brandConfig.brandId)}>
          Add Row
        </Button>
      </div>

      {/* Confirmation Dialog for Pixel Unselection */}
      <Dialog
        open={!!pendingPixelUnselection}
        onOpenChange={open => !open && cancelPixelUnselection()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unselect Pixel?</DialogTitle>
            <DialogDescription>
              <span className="block">
                Are you sure you want to unselect this pixel? This will remove all threshold
                combinations associated with it.
              </span>
            </DialogDescription>
          </DialogHeader>
          {pendingPixelUnselection && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-2">
              <p>
                <span className="font-semibold">Pixel(s) to unselect:</span>
              </p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                {pendingPixelUnselection.removedPixels.map(pixelId => {
                  const pixel = pixels.find(p => p.pixel_id === pixelId);
                  return <li key={pixelId}>{pixel?.pixel_name || pixelId}</li>;
                })}
              </ul>
              <p className="pt-2 border-t border-gray-200">
                <span className="font-semibold">
                  {pendingPixelUnselection.affectedThresholdCount} threshold
                  {pendingPixelUnselection.affectedThresholdCount !== 1 ? 's' : ''} will be removed.
                </span>
              </p>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={cancelPixelUnselection}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmPixelUnselection}>
              Unselect Pixel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};